import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import { IUser } from "../Users"
import useSWR, { SWRConfiguration } from "swr"

export type TSheetFolder<TData = unknown> = {
	id: number
	name: string
	description?: string
	created_at: string
	created_by?: IUser
	files: Array<TSheetFile<TData>>
}

export type TSheetFile<TData = unknown> = {
	id: number
	name: string // The File's Name
	contains: "transport_services" | "cab_price_calculation_metrics"
	description: string | null // Description of the file, optional
	created_at: string
	created_by?: IUser
	updated_at: string | null
	updated_by?: IUser
	deleted_at: string | null
	deleted_by?: IUser
	folder?: TSheetFolder // Only available when loading full details of a file
	latest_version: TSheetFileVersion<TData> // Only available include is given
	versions: Array<TSheetFileVersion<TData>>
}

export type TSheetFileVersion<TData> = {
	id: number
	is_draft: boolean // indicates that the version is in draft stage
	created_at: string
	created_by?: IUser
	uploaded_at: string | null
	uploaded_by?: IUser
	file?: TSheetFile // Only available when loading full details of a file
	data?: TData // Only available when loading full details of a file
}

export interface ISheetFolderForm {
	name: string
	description?: string
}

export interface ISheetFileForm {
	name: string
	description: string | null
}

export type TCreateSheetFolderPayload = ISheetFolderForm

export type TUpdateSheetFolderPayload = ISheetFolderForm

export type TUpdateSheetFilePayload = ISheetFileForm

export enum SheetFormSubmissionAction {
	UPLOAD = "UPLOAD",
	SAVE_DRAFT = "SAVE_DRAFT",
}

export function dataDriveXHR(xhr: XHRInstance) {
	return {
		async getFolders(
			params?: Record<string, unknown>
		): Promise<IListResponse<TSheetFolder>> {
			return xhr
				.get("/data-drive/folders", { params: { ...params } })
				.then((resp) => resp.data)
		},
		async getFolderDetails(folderId: string): Promise<TSheetFolder> {
			return await xhr
				.get(`/data-drive/folders/${folderId}`)
				.then((resp) => resp.data.data)
		},
		async updateFolder(
			folderId: string,
			folderData: ISheetFolderForm
		): Promise<TSheetFolder> {
			return xhr
				.post(`/data-drive/folders/${folderId}`, {
					...folderData,
				})
				.then((resp) => resp.data.data)
		},

		async createFolder(folderData: ISheetFolderForm): Promise<TSheetFolder> {
			return xhr
				.post(`/data-drive/folders`, {
					...folderData,
				})
				.then((resp) => resp.data.data)
		},

		// files
		async getFileDetails(fileId: string): Promise<TSheetFile> {
			return xhr
				.get(`/data-drive/files/${fileId}`)
				.then((resp) => resp.data.data)
		},
		async updateFile(
			fileId: string,
			folderData: ISheetFileForm
		): Promise<TSheetFile> {
			const editFilePayloadData: TUpdateSheetFilePayload = {
				name: folderData.name,
				description: folderData.description || null,
			}
			return xhr
				.post(`/data-drive/files/${fileId}`, {
					...editFilePayloadData,
				})
				.then((resp) => resp.data.data)
		},
		async deleteFile(fileId: string): Promise<{ message: string }> {
			return xhr
				.delete(`/data-drive/files/${fileId}`)
				.then((resp) => resp.data.data)
		},
		//versions
	}
}

export function getCacheKeyForFolderDetails(folderId: number | string) {
	return `/data-drive/${folderId}`
}
export function useFolderDetails(folderId?: string, config?: SWRConfiguration) {
	const xhr = useXHR()
	const {
		data: folderDetails,
		mutate: refreshFolderDetails,
		error,
		isValidating,
	} = useSWR<TSheetFolder>(
		folderId ? getCacheKeyForFolderDetails(folderId) : null,
		() =>
			folderId
				? dataDriveXHR(xhr).getFolderDetails(folderId)
				: Promise.reject(),
		config
	)

	return { folderDetails, refreshFolderDetails, error, isValidating }
}
export function useFileDetails(fileId: string, config?: SWRConfiguration) {
	const xhr = useXHR()
	const {
		data: fileDetails,
		mutate: refreshFileDetails,
		error,
		isLoading,
	} = useSWR<TSheetFile>(
		`data-drive/${fileId}`,
		() => dataDriveXHR(xhr).getFileDetails(fileId),
		config
	)

	return { fileDetails, refreshFileDetails, error, isLoading }
}
